import { Activity, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  Popup,
  PermissionDenied,
  PermissionEnum,
  SidePanelLoader,
  Tabs,
  ChangeLogs,
  IconExpand,
  ButtonSize,
} from '@ui/src';
import { useAppSelector } from '@store';
import useFormFunctions from './hooks/use-form';
import ExternalAccountForm from '../../../components/external-accounts/form';
import ExternalAccountTopUpOptionItem from '../../../components/external-accounts/top-op-option';

import cn from '../styles.module.css';
import { ExternalAccountTopUpOptionFormValues } from '../types';

const CreateExternalAccount = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const { search } = useLocation();
  const { accountId } = useParams();

  const {
    pspPartners,
    takeCount,
    casp,
    externalAccount,
    externalAccountTopUpOptions,
    changeLogs,
    changeLogsPagination,
  } = useAppSelector((state) => state.externalAccounts);
  const { activePermissions, paymentTypes, currenciesList } = useAppSelector(
    (state) => state.root,
  );
  const { users } = useAppSelector((state) => state.administrationDictionaries);

  const {
    selectedPartner,
    setSelectedPartner,
    balances,
    globalError,
    isLoading,
    accounts,
    onSearchAccounts,
    onClear,
    accountsIsLoading,
    control,
    errors,
    isDirty,
    setValue,
    pspPartnerId,
    paymentType,
    currencyId,
    onSubmit,
    handleClose,
    availablePaymentTypes,
    balancesIsLoading,
    balancesIsFetching,
    updateStatusInProgress,
    accountIdValue,
    externalAccountIsLoading,
    isOpenPayd,
    integrationType,
    setIntegrationType,
    openPaydKeelIntegration,
    onSubmitWithIntegration,
    createInProgress,
    activeTab,
    setActiveTab,
    topUpIsLoading,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    updateExternalAccountCurrenciesInProgress,
    caspId,
    isPrimeVault,
    setIsOpenPayd,
    setIsPrimeVault,
    isKeel,
    setIsKeel,
    setSelectedOptionId,
    deleteConfirmationVisible,
    setDeleteConfirmationVisible,
    deletePaymentAccountsAction,
    deletePaymentAccountsInProgress,
    topUpOptions,
    setTopUpOptions,
    createPaymentAccountsInProgress,
    updatePaymentAccountsInProgress,
    optionsErrors,
    setOptionsErrors,
  } = useFormFunctions({
    takeCount,
    search,
    setPanelIsOpen,
    pspPartners,
    accountId,
    paymentTypes,
    externalAccount,
    externalAccountTopUpOptions,
  });

  let content = <div />;

  if (activePermissions) {
    if (
      activePermissions.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
      )
    ) {
      if (accountId && externalAccountIsLoading) {
        content = <SidePanelLoader />;
      } else {
        content = (
          <>
            {accountId &&
              (Boolean(
                externalAccount?.pspIntegrationPaymentAccountsCount &&
                activePermissions?.includes(
                  PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
                ),
              ) ||
                activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) && (
                <Tabs
                  tabs={[
                    {
                      name: 'Details',
                      value: 'details',
                    },
                    {
                      name: 'Top-up options',
                      value: 'top-up',
                      hidden:
                        Boolean(
                          externalAccount?.createType === 'AUTO' &&
                          !externalAccount?.pspIntegrationPaymentAccountsCount,
                        ) ||
                        !activePermissions?.includes(
                          PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
                        ),
                    },
                    {
                      name: 'Change Log',
                      value: 'change-logs',
                      hidden: !activePermissions?.includes(
                        PermissionEnum.AUDIT_LOG_LIST,
                      ),
                    },
                  ]}
                  activeTab={activeTab}
                  onClick={(tab) =>
                    setActiveTab(tab as 'details' | 'top-up' | 'change-logs')
                  }
                  wrapperClassName={cn.externalAccountTabs}
                />
              )}
            <Activity mode={activeTab === 'details' ? 'visible' : 'hidden'}>
              <ExternalAccountForm
                externalAccount={externalAccount}
                availablePaymentTypes={availablePaymentTypes}
                control={control}
                errors={errors}
                pspPartnerId={pspPartnerId}
                paymentTypes={paymentTypes}
                paymentType={paymentType}
                setSelectedPartner={setSelectedPartner}
                pspPartners={pspPartners?.filter((item) => item.active)}
                selectedPartner={selectedPartner}
                setValue={setValue}
                currencyId={currencyId}
                accounts={accounts}
                onSearchAccounts={onSearchAccounts}
                onClear={onClear}
                accountsIsLoading={accountsIsLoading}
                balances={balances}
                accountId={accountId}
                customerAccountId={accountIdValue}
                casp={casp?.map((item) => ({
                  name: item.name,
                  value: item.id,
                }))}
                isOpenPayd={isOpenPayd}
                isKeel={isKeel}
                integrationType={integrationType}
                setIntegrationType={setIntegrationType}
                openPaydIntegration={openPaydKeelIntegration}
                balancesIsLoading={balancesIsLoading || balancesIsFetching}
                isPrimeVault={isPrimeVault}
                caspId={caspId}
                caspValues={casp}
                setIsOpenPayd={setIsOpenPayd}
                setIsPrimeVault={setIsPrimeVault}
                setIsKeel={setIsKeel}
              />
            </Activity>
            <Activity mode={activeTab === 'top-up' ? 'visible' : 'hidden'}>
              <>
                {topUpIsLoading && <SidePanelLoader />}
                {!topUpIsLoading && (
                  <>
                    <div className={cn.topUpOptionsTitleWrapper}>
                      <div className={cn.topUpOptionsTitleWrapperLeft}>
                        <div className={cn.topUpOptionsTitle}>
                          Top-up options
                        </div>
                        <div className={cn.topUpOptionsDescription}>
                          Payment details the customer uses to top up this
                          account
                        </div>
                      </div>
                      {externalAccount?.createType === 'MANUAL' &&
                        activePermissions?.includes(
                          PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_ADD_ACTION,
                        ) && (
                          <ButtonSecondary
                            className={`${cn.createOptionButton} button-white`}
                            icon={<IconExpand />}
                            text="Add option"
                            size={ButtonSize.SMALL}
                            onClick={() => {
                              const nextOptions = (
                                [] as ExternalAccountTopUpOptionFormValues[]
                              ).concat(topUpOptions);

                              nextOptions.unshift({
                                id: `option-${uuid()}`,
                                paymentType: '',
                                currencyId: '',
                                bankAccountHolderName: '',
                                accountNumber: '',
                                bankAddress: '',
                                bic: '',
                                iban: '',
                                thirdPartyReference: '',
                                bankName: '',
                                routingCodeEntries: [],
                              });

                              setTopUpOptions(nextOptions);
                            }}
                          />
                        )}
                    </div>
                    {!topUpOptions?.length && (
                      <div className={cn.emptyTopUpOption}>
                        No top up options yet
                      </div>
                    )}
                    {topUpOptions &&
                      topUpOptions?.length > 0 &&
                      topUpOptions?.map((item) => (
                        <ExternalAccountTopUpOptionItem
                          key={item.id}
                          topUpOption={item}
                          paymentTypes={paymentTypes}
                          currencies={currenciesList}
                          setSelectedOptionId={setSelectedOptionId}
                          setDeleteConfirmationVisible={
                            setDeleteConfirmationVisible
                          }
                          activePermissions={activePermissions}
                          isEditable={
                            externalAccount?.createType === 'MANUAL' &&
                            activePermissions?.includes(
                              PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_EDIT_ACTION,
                            )
                          }
                          newOption={Boolean(item?.id?.includes('option'))}
                          setTopUpOptions={setTopUpOptions}
                          topUpOptions={topUpOptions}
                          optionsError={optionsErrors?.find(
                            (error) => error?.id === item.id,
                          )}
                          optionsErrors={optionsErrors}
                          setOptionsErrors={setOptionsErrors}
                        />
                      ))}
                  </>
                )}
              </>
            </Activity>
            <Activity mode={activeTab === 'change-logs' ? 'visible' : 'hidden'}>
              <>
                {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                  <ChangeLogs
                    entityType="PSP_INTEGRATION_ACCOUNT"
                    entityId={accountId as string}
                    changeLogsIsLoading={changeLogsIsLoading}
                    changeLogs={changeLogs}
                    activePermission={activePermissions}
                    users={users}
                    pagination={changeLogsPagination}
                    changeLogsIsFetching={changeLogsIsFetching}
                    getChangeLogs={getChangeLogsAction}
                  />
                )}
                {!activePermissions?.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                ) && <PermissionDenied />}
              </>
            </Activity>
          </>
        );
      }
    } else {
      content = <PermissionDenied />;
    }
  }

  let buttonText;

  if (
    updateStatusInProgress ||
    isLoading ||
    createInProgress ||
    updateExternalAccountCurrenciesInProgress ||
    createPaymentAccountsInProgress ||
    updatePaymentAccountsInProgress
  ) {
    buttonText = 'Loading...';
  } else {
    buttonText = 'Add account';

    if (accountId) {
      buttonText = 'Save';
    }
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title={accountId ? 'External Account settings' : 'Add External Account'}
        errorSidepanel={globalError}
        sidepanelExpandedKey="external-create"
        footer={
          <>
            <ButtonPrimary
              text={buttonText}
              disabled={
                (!accountId && !isDirty) ||
                isLoading ||
                updateStatusInProgress ||
                createInProgress ||
                updateExternalAccountCurrenciesInProgress ||
                createPaymentAccountsInProgress ||
                updatePaymentAccountsInProgress
              }
              onClick={() =>
                integrationType === 'with-integration' && !accountId
                  ? onSubmitWithIntegration()
                  : onSubmit()
              }
              bold
            />
            <ButtonSecondary
              text="Close"
              className="button-white"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="Delete this option?"
        description=""
        onCancelClick={() => {
          setDeleteConfirmationVisible(false);
          setSelectedOptionId('');
        }}
        onOkClick={deletePaymentAccountsAction}
        cancelButtonText="Cancel"
        okButtonText={
          deletePaymentAccountsInProgress ? 'Loading..' : 'Yes, Delete'
        }
        okButtonDisabled={deletePaymentAccountsInProgress}
        isVisible={deleteConfirmationVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={() => {
          setDirtyPopupVisible(false);
          handleClose();
        }}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />

      <div id="dirty-popup" />
    </>
  );
};

export default CreateExternalAccount;
